import { Burger, Flex, Group, Text, ThemeIcon } from "@mantine/core";
import { FileSpreadsheet, Home } from "lucide-react";
import { useLocation } from "react-router-dom";
import useHeaderStore from "../../store/layout/headerStore";
import AccountViewHeaderContent from "./AccountViewHeaderContent";
import UserViewHeaderContent from "./UserViewHeaderContent";
import ViewHeaderContent from "./ViewHeaderContent";
import { BoltIcon, ChatBubbleBottomCenterTextIcon } from "@heroicons/react/20/solid";

interface DynamicHeaderProps {
  opened: boolean;
  toggle: () => void;
}

const DynamicHeader = ({ opened, toggle }: DynamicHeaderProps) => {
  const { title, description, actions } = useHeaderStore();
  const location = useLocation();
  
  // Check for specific view paths
  const isAccountViewPath = /\/home\/[\w\d]+\/view\/.+\/account\/.+/i.test(location.pathname);
  const isUserViewPath = (
    /\/home\/[\w\d]+\/account\/(.+)\/user\/(.+)/i.test(location.pathname) || 
    /\/home\/[\w\d]+\/view\/[\w\d-]+\/user\/[\w\d-]+/i.test(location.pathname)
  );
  const isViewPath = /\/home\/[\w\d]+\/view\/[\w\d-]+/i.test(location.pathname);
  const isExportsPath = /\/home\/[\w\d]+\/exports/i.test(location.pathname);
  const isHQPath = /\/home\/[\w\d]+\/headquarters/i.test(location.pathname);
  const isInboxPath = /\/home\/[\w\d]+\/inbox/i.test(location.pathname);
  const isNewSignalPath = /\/home\/[\w\d]+\/signals\/new/i.test(location.pathname);
  const isEditSignalPath = /\/home\/[\w\d]+\/signals\/[\w\d-]+\/edit/i.test(location.pathname);
  const isSignalsPath = /\/home\/[\w\d]+\/signals/i.test(location.pathname) && !isNewSignalPath && !isEditSignalPath;

  // If it's an account view path, render account view header
  if (isAccountViewPath) {
    return (
      <Group h="100%" justify="space-between">
        <Group>
          <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
          <AccountViewHeaderContent />
        </Group>
      </Group>
    );
  }

  // If it's inbox path, render inbox header
  if (isInboxPath) {
    return (
      <Group h="100%" justify="space-between">
        <Group>
          <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
          <Flex gap={5} align="center">
            <ThemeIcon size="md" radius="md" variant="outline">
              <ChatBubbleBottomCenterTextIcon className="w-5 h-5" />
            </ThemeIcon>
            <Text fw={600} size="sm">Inbox</Text>
          </Flex>
        </Group>
      </Group>
    );
  }

  // If it's HQ path, render HQ header
  if (isHQPath) {
    return (
      <Group h="100%" justify="space-between">
        <Group>
          <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
          <Flex gap={5} align="center">
            <ThemeIcon size="md" radius="md" variant="outline">
              <Home size={20} />
            </ThemeIcon>
            <Text fw={600} size="sm">Headquarters</Text>
          </Flex>
        </Group>
      </Group>
    );
  }
  
  // If it's a user view path, render user view header
  if (isUserViewPath) {
    return (
      <Group h="100%" justify="space-between">
        <Group>
          <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
          <UserViewHeaderContent />
        </Group>
      </Group>
    );
  }

  // If it's a general view path, render view header
  if (isViewPath) {
    return (
      <Group h="100%" justify="space-between">
        <Group>
          <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
          <ViewHeaderContent />
        </Group>
      </Group>
    );
  }
  
  // If it's the exports page, render exports header
  if (isExportsPath) {
    return (
      <Group h="100%" justify="space-between">
        <Group gap={3}>
          <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
          <ThemeIcon size="md" radius="md" variant="light" color="blue">
            <FileSpreadsheet size={20} />
          </ThemeIcon>
          <Text fw={600} size="sm">Export History</Text>
        </Group>
      </Group>
    );
  }
  
  // If it's the signals page, set title to "Your Signals"
  if (isSignalsPath) {
    return (
      <Group h="100%" justify="space-between">
        <Group>
          <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
          <Flex gap={5} align="center">
            <ThemeIcon size="md" radius="md" variant="outline">
              <BoltIcon className="w-4 h-4" />
            </ThemeIcon>
            <Text fw={600} size="sm">Signal Manager</Text>
          </Flex>
        </Group>
        {actions && (
          <Group>
            {actions}
          </Group>
        )}
      </Group>
    );
  }
  
  // If it's the edit signal page
  if (isEditSignalPath) {
    return (
      <Group h="100%" justify="space-between">
        <Group>
          <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
          <Flex gap={5} align="center">
            <ThemeIcon size="md" radius="md" variant="outline">
              <BoltIcon className="w-4 h-4" />
            </ThemeIcon>
            <Text fw={600} size="sm">Edit signal</Text>
          </Flex>
        </Group>
        {actions && (
          <Group>
            {actions}
          </Group>
        )}
      </Group>
    );
  }
  // If it's the new signal page, set title to "Create new signal"
  if (isNewSignalPath) {
    return (
      <Group h="100%" justify="space-between">
        <Group>
          <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
          <Flex gap={5} align="center">
            <ThemeIcon size="md" radius="md" variant="outline">
              <BoltIcon className="w-4 h-4" />
            </ThemeIcon>
            <Text fw={600} size="sm">Create new signal</Text>
          </Flex>
        </Group>
        {actions && (
          <Group>
            {actions}
          </Group>
        )}
      </Group>
    );
  }
  
  return (
    <Group h="100%" justify="space-between">
      <Group>
        <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
        {title && (
          <div>
            <Text fw={600} size="sm">{title}</Text>
            {description && <Text size="xs" c="dimmed">{description}</Text>}
          </div>
        )}
      </Group>
      
      {actions && (
        <Group>
          {actions}
        </Group>
      )}
    </Group>
  );
};

export default DynamicHeader;
